import { useEffect, useRef, type RefObject } from 'react'

import { EMBED_MESSAGE_TYPES, type IIframeHeightMessage } from 'services/IframeMessaging'

interface IUseEmbedResizeNotifierOptions {
  enabled: boolean
  debounceMs?: number
  iframeId?: string
}

/**
 * Sends height updates to parent window for iframe resizing.
 * Used when Parta course is embedded in external platforms (Notion, GitBook, etc.)
 *
 * Uses ResizeObserver for efficient DOM monitoring.
 * Debounces updates to avoid flooding parent.
 */
export const useEmbedResizeNotifier = (
  containerRef: RefObject<HTMLElement>,
  options: IUseEmbedResizeNotifierOptions,
) => {
  const { enabled, debounceMs = 100, iframeId } = options
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const lastHeightRef = useRef<number>(0)

  useEffect(() => {
    if (!enabled || !containerRef.current) {
      return
    }

    if (window.parent === window) {
      return
    }

    const sendHeight = () => {
      const heights = [
        document.body.scrollHeight,
        document.body.offsetHeight,
        document.documentElement.scrollHeight,
        document.documentElement.offsetHeight,
      ].filter((h) => h > 0)

      if (heights.length === 0) {
        return
      }

      const height = Math.max(...heights)

      if (height === lastHeightRef.current) {
        return
      }

      lastHeightRef.current = height

      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }

      debounceTimerRef.current = setTimeout(() => {
        try {
          const message: IIframeHeightMessage = {
            type: EMBED_MESSAGE_TYPES.IFRAME_HEIGHT,
            height,
            ...(iframeId && { iframeId }),
          }
          window.parent.postMessage(message, '*')
        } catch {
          // Ignore cross-origin errors
        }
      }, debounceMs)
    }

    sendHeight()

    window.addEventListener('load', sendHeight)
    window.addEventListener('resize', sendHeight)

    let resizeObserver: ResizeObserver | null = null
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(sendHeight)
      resizeObserver.observe(document.body)
      if (containerRef.current) {
        resizeObserver.observe(containerRef.current)
      }
    }

    let mutationObserver: MutationObserver | null = null
    mutationObserver = new MutationObserver(sendHeight)
    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      characterData: true,
    })

    return () => {
      window.removeEventListener('load', sendHeight)
      window.removeEventListener('resize', sendHeight)
      resizeObserver?.disconnect()
      mutationObserver?.disconnect()
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [enabled, containerRef, debounceMs, iframeId])
}
